import { Container } from '@almbrand/container';
import { Grid } from '@almbrand/grid';
import { RichText } from '@almbrand/richtext';
import { IconsProps } from '@almbrand/systemicons/dist/cjs/types/icon';
import { Tag } from '@almbrand/tag';
import { CtaGroupComponent, HeadlineComponent } from 'components';
import { ThemeNames } from 'constantsValues';
import { memo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'store/index';
import { selectBasketItems, updateItemQuantity } from 'store/slices/basketSlice';
import styles from './ProductCalculatorSelectorTemplate.module.scss';

type CodanIcon = IconsProps<'Codan-theme'>['icon'];

const productImageIconMap: Record<string, CodanIcon> = {
	'https://beregn.codan.dk/codan/codan-media/icons-svg/private/products/icon-product-rejse.svg': 'Plane',
	'https://beregn.codan.dk/codan/codan-media/icons-svg/private/products/icon-product-ulykke.svg': 'briefcase_plus',
	'https://beregn.codan.dk/codan/codan-media/icons-svg/private/products/icon-product-indbo.svg': 'lamp',
	'https://beregn.codan.dk/codan/codan-media/icons-svg/private/products/icon-product-hus.svg': 'house',
	'https://beregn.codan.dk/codan/codan-media/icons-svg/private/products/icon-product-fritidshus.svg': 'house_flag',
	'https://beregn.codan.dk/codan/codan-media/icons-svg/private/products/icon-product-bil.svg': 'car',
};

const getProductIcon = (imageUrl?: string): CodanIcon => {
	return imageUrl ? (productImageIconMap[imageUrl] ?? 'house') : 'house';
};

const getProductId = (
	product: NonNullable<ProductCalculatorSelectorTemplateModel['products']>[number],
	index: number
): string => {
	return product.salesProduct ?? product.url ?? `${index}`;
};

const PRODUCT_QUANTITY_OPTIONS = ['0', '1', '2', '3'].map((optionValue) => ({
	value: optionValue,
	label: optionValue,
}));

export interface ProductCalculatorSelectorTemplateProps {
	pageModel: ProductCalculatorSelectorTemplateModel;
}

export const ProductCalculatorSelectorTemplate: React.FC<ProductCalculatorSelectorTemplateProps> = memo(
	({ pageModel }) => {
		// The generic PageModel does not carry the selector-specific fields; this
		// template is only rendered when contentType === 'ProductCalculatorSelectorTemplate',
		// so it is safe to narrow to the dedicated model here.
		const themeName = ThemeNames[pageModel.theme];
		const title = pageModel?.title;
		const description = pageModel?.description;
		const products = pageModel?.products ?? [];
		const basketItems = useAppSelector(selectBasketItems);
		const dispatch = useAppDispatch();

		//TODO: Remove when pushing to main
		useEffect(() => {
			console.log('Selected product quantities updated: ', basketItems);
		}, [basketItems]);

		const getQuantityFromTagValue = (productId: string, value: string | undefined): number => {
			if (!value) {
				return 0;
			}

			const parsedQuantity = Number.parseInt(value, 10);
			if (!Number.isNaN(parsedQuantity)) {
				return Math.max(parsedQuantity, 0);
			}

			return value === productId ? 1 : 0;
		};

		const onTagValueChange = (
			product: NonNullable<ProductCalculatorSelectorTemplateModel['products']>[number],
			productId: string,
			value: string | undefined
		): void => {
			const quantity = getQuantityFromTagValue(productId, value);

			dispatch(
				updateItemQuantity({
					item: {
						id: productId,
						name: product.title ?? '',
						url: product.url ?? '',
					},
					quantity,
				})
			);
		};

		return (
			<div
				data-page-type='ProductCalculatorSelectorTemplate'
				className={styles.ProductCalculatorSelectorTemplate}
			>
				<Container
					mobileWidth='fullWidth'
					noPadding
				>
					<Grid nogutter>
						<div className={styles.ProductCalculatorSelectorTemplate__headline}>
							<HeadlineComponent
								title={title}
								heading='h3'
								size='default'
								fontColor='primary'
							/>

							{description && <RichText description={description} />}
						</div>
					</Grid>
				</Container>

				{products.length > 0 && (
					<ul
						data-testid='product-calculator-selector-products'
						className={styles.ProductCalculatorSelectorTemplate__productList}
					>
						{products.map((product, index) => {
							const productId = getProductId(product, index);

							const options = product.maxQuantity
								? Array.from({ length: product.maxQuantity + 1 }, (_, index) => {
										const value = index.toString();

										return {
											value,
											label: value,
										};
									})
								: undefined;

							return (
								<li key={productId}>
									<Tag
										iconProps={{
											themeName: themeName,
											icon: getProductIcon(product.imageUrl) as never,
										}}
										checkboxValue={productId}
										optionValue={basketItems
											.filter((item) => item.id === productId)
											.length.toString()}
										title={product.title}
										onValueChange={(value) => {
											onTagValueChange(product, productId, value);
										}}
										checked={basketItems.some((item) => item.id === productId)}
										{...(options ? { options } : {})}
										optionsCount
									/>
								</li>
							);
						})}
					</ul>
				)}
				<CtaGroupComponent
					ctaGroup={{
						items: [
							{
								content: {
									isValid: false,
									label: 'back',
								},
							},
							{
								content: {
									isValid: false,
									label: 'Næste',
									buttonActionType: 'submit',
								},
							},
						],
					}}
					pageId='123456'
				/>
			</div>
		);
	}
);

ProductCalculatorSelectorTemplate.displayName = 'ProductCalculatorSelectorTemplate';
